import React from 'react';
import { Button } from '@/components/ui/button';
import { AlertTriangle, RefreshCw, Home } from 'lucide-react';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  componentStack: string | null;
}

export class ErrorBoundary extends React.Component<
  { children: React.ReactNode },
  ErrorBoundaryState
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error, componentStack: null };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('ErrorBoundary caught:', error, errorInfo);
    this.setState({ componentStack: errorInfo.componentStack ?? null });
  }

  handleGoHome = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
    window.location.href = '/';
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (this.state.hasError) {
      const { error, componentStack } = this.state;
      const stackLines = (error?.stack ?? '').split('\n').slice(0, 8).join('\n');
      const compStack = (componentStack ?? '').split('\n').slice(0, 6).join('\n');

      return (
        <div className="min-h-screen flex items-center justify-center bg-background p-6">
          <div className="text-center space-y-4 max-w-lg w-full">
            <AlertTriangle className="w-16 h-16 text-destructive mx-auto" />
            <h1 className="text-2xl font-bold text-foreground">Đã xảy ra lỗi</h1>
            <p className="text-muted-foreground">
              Ứng dụng gặp sự cố không mong muốn. Vui lòng thử tải lại trang.
            </p>

            {error && (
              <details className="text-left bg-muted/40 border border-border rounded-lg p-3 text-xs">
                <summary className="cursor-pointer font-medium text-foreground">
                  Chi tiết lỗi (gửi cho hỗ trợ)
                </summary>
                <div className="mt-2 space-y-2">
                  <div>
                    <div className="font-semibold text-destructive">{error.name}: {error.message}</div>
                  </div>
                  {stackLines && (
                    <pre className="whitespace-pre-wrap break-words text-muted-foreground overflow-x-auto">
{stackLines}
                    </pre>
                  )}
                  {compStack && (
                    <pre className="whitespace-pre-wrap break-words text-muted-foreground overflow-x-auto border-t border-border pt-2">
{compStack}
                    </pre>
                  )}
                </div>
              </details>
            )}

            <div className="flex gap-2 justify-center flex-wrap">
              <Button onClick={this.handleReload} variant="default" className="gap-2">
                <RefreshCw className="w-4 h-4" />
                Tải lại trang
              </Button>
              <Button onClick={this.handleGoHome} variant="outline" className="gap-2">
                <Home className="w-4 h-4" />
                Về trang chủ
              </Button>
            </div>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
